import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  TablePagination,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { API_BASE_URL } from 'src/apiConfig';
import { RoleContext } from 'src/context/roleContext';
import { useTranslation } from 'react-i18next';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';

import {
  FLAG_FINALE,
  FLAG_REZULTATI,
  FLAG_ZALBE,
  modeDescriptions
} from 'src/constants/competitions';
import { latinToCyrillic } from 'src/helper/helperFunction';
import {
  StyledTabs,
  TabsWrapper
} from 'src/components/styledHelpComponents/styledHelpComponents';

const fetchStudents = async (schoolId, filters) => {
  const params = new URLSearchParams({
    ...filters,
    school_id_filter: schoolId
  });
  const response = await fetch(
    `${API_BASE_URL}/api/students.php?getStudentByFilter=true&${params.toString()}`
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

// STRANA REZULTATA
const ResultsPage = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [selectedTab, setSelectedTab] = useState(1);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const { schoolId } = useContext(RoleContext);
  const { activeModes } = useContext(ActiveModesContext);
  const activeModeKey = activeModes.find((mode) => modeDescriptions[mode]);

  const activeMode = activeModeKey
    ? t(`modes.${activeModeKey}`, { defaultValue: activeModeKey })
    : t('noActiveMode');

  const isResultsAccessAllowed =
    activeModes.includes(FLAG_REZULTATI) ||
    activeModes.includes(FLAG_ZALBE) ||
    activeModes.includes(FLAG_FINALE);

  useEffect(() => {
    if (isResultsAccessAllowed) {
      const filters = {
        classFilter: selectedTab
      };

      fetchStudents(schoolId, filters)
        .then((data) => {
          const filteredData = data.filter(
            (student) => parseFloat(student.point_sum) !== 0
          );
          setStudents(filteredData);
        })
        .catch((error) => console.error('Fetch error:', error));
    }
  }, [schoolId, selectedTab, isResultsAccessAllowed]);

  const filteredStudents = students.filter((student) => {
    const latinSearch = searchQuery.toLowerCase();
    const cyrillicSearch = latinToCyrillic(searchQuery).toLowerCase();

    return (
      student.name.toLowerCase().includes(latinSearch) ||
      student.name.toLowerCase().includes(cyrillicSearch)
    );
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0); // Resetuje na prvu stranu pri promeni taba
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const sortedStudents = filteredStudents.sort(
    (a, b) => b.point_sum - a.point_sum
  );
  const paginatedStudents = sortedStudents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const chartData = sortedStudents.slice(0, 10).map((student) => ({
    name: student.name,
    points: student.points
  }));

  const exportToExcel = () => {
    const exportData = students.map((student) => ({
      [t('results.name')]: student.name,
      [t('statistic.grade')]: student.class,
      [t('results.points')]: student.points,
      [t('columns.professor')]: student.professor
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

    XLSX.writeFile(
      workbook,
      `Rezultati_
      _razred_${selectedTab}.xlsx`
    );
  };

  const chartBarColor = '#2596be';

  if (!isResultsAccessAllowed) {
    return (
      <Box
        display="grid"
        gap={3}
        p={2}
        sx={{
          backgroundColor: '#e0f7fa',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" color="textSecondary">
          {t('results.accessDenied')} - <b>{activeMode}</b>
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Poravnanje naslova i dugmića */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t('results.title')}
        </Typography>

        {/* Dugmići za izvoz */}
        <Box
          sx={{
            display: 'flex',
            gap: '10px'
          }}
        >
          <Button
            variant="contained"
            onClick={exportToExcel}
            sx={{
              backgroundColor: '#25be46',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#1c75bc'
              },
              borderRadius: '8px',
              fontSize: '0.875rem',
              boxShadow: 'none',
              textTransform: 'none',
              height: '40px'
            }}
            startIcon={<DownloadIcon />}
          >
            {t('result.exportExcel')}
          </Button>
        </Box>
      </Box>

      {/* Tabovi za razrede */}
      <TabsWrapper>
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={t('results.tabsLabel')}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}
        >
          {[...Array(12).keys()].map((num) => (
            <Tab
              label={t('results.grade', { grade: num + 1 })}
              value={num + 1}
              key={num}
              sx={{
                fontWeight: selectedTab === num + 1 ? 'bold' : 'normal',
                fontSize: '0.9rem',
                padding: '8px 16px',
                backgroundColor:
                  selectedTab === num + 1 ? '#2196f3' : '#ffffff',
                color: selectedTab === num + 1 ? '#ffffff' : '#333',
                '&:hover': {
                  backgroundColor:
                    selectedTab === num + 1 ? '#1976d2' : '#f0f0f0'
                },
                borderRadius: '8px',
                transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s'
              }}
            />
          ))}
        </StyledTabs>
      </TabsWrapper>
      {/* Pretraga */}
      <TextField
        variant="outlined"
        placeholder={t('search.placeholder')}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{
          width: '100%',
          backgroundColor: '#f5f7fa',
          borderRadius: '8px',
          marginBottom: '20px'
        }}
      />

      {isResultsAccessAllowed && filteredStudents.length > 0 ? (
        <>
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              maxHeight: 500,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px'
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#2596be' }}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: '#2596be'
                    }}
                  >
                    {t('results.name')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: '#2596be'
                    }}
                  >
                    {t('statistic.grade')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: '#2596be'
                    }}
                  >
                    {t('results.points')}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: '#2596be'
                    }}
                  >
                    {t('form.location')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedStudents.map((student) => (
                  <TableRow key={`${student.id}-${student.name}`} hover>
                    <TableCell align="center">{student.name}</TableCell>
                    <TableCell align="center">{student.class}</TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 'bold', color: '#1565c0' }}
                    >
                      {student.points}
                    </TableCell>
                    <TableCell align="center">{student.place}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredStudents.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('pagination.of')} ${
                count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
              }`
            }
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              marginTop: '10px',
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold'
              }
            }}
          />
        </>
      ) : (
        <Typography variant="h6" color="textSecondary" mt={3}>
          {t('results.noData')}
        </Typography>
      )}

      <Box
        sx={{
          height: '2px',
          backgroundColor: '#dcdcdc',
          margin: '20px 0'
        }}
      />

      {isResultsAccessAllowed && chartData.length > 0 && (
        <Box
          sx={{
            height: 300,
            mt: 3,
            backgroundColor: '#f0f4f8',
            padding: 2,
            borderRadius: '12px',
            boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis
                label={{
                  value: t('results.points'),
                  angle: -90,
                  position: 'insideLeft'
                }}
              />
              <Tooltip
                formatter={(value) => [`${value}`, t('results.points')]}
              />
              <Bar dataKey="points" fill={chartBarColor} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default ResultsPage;
